import { Pill, SelectableTile, Tooltip, Typography } from "@fullscript/aviary-web";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { useMFAData } from "@shared/hooks/useMFAData/useMFAData";
import { l } from "@shared/locales/i18n";
import { TwoFactorAuthenticationMethods as AuthMethod } from "@shared/types/graphqlGenerated";

import * as styles from "./ChooseAuthenticationMethod.styles";

interface Props {
  selectedAuthMethod: AuthMethod;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
}

const ChooseAuthenticationMethod = ({ selectedAuthMethod, onSelect }: Props) => {
  const { t } = useTranslation();
  const { isMFAEnabled, otpMethod } = useMFAData();

  const isEmailDisabled = isMFAEnabled && otpMethod === AuthMethod.Email;

  const renderAuthMethod = (method: AuthMethod) => {
    switch (method) {
      case AuthMethod.Sms:
        return (
          <SelectableTile
            aria-label={t(l.mfa.chooseAuthenticationMethod.useTextMessage)}
            name={AuthMethod.Sms}
            onChange={onSelect}
            checked={selectedAuthMethod === AuthMethod.Sms}
            value={AuthMethod.Sms}
            input="radio"
            label={t(l.mfa.chooseAuthenticationMethod.useTextMessage)}
            customCss={styles.tile}
          >
            <Typography>
              {t(l.mfa.chooseAuthenticationMethod.receiveOneTimeCodeViaTextMessage)}
            </Typography>
          </SelectableTile>
        );
      case AuthMethod.Email:
        return (
          <SelectableTile
            aria-label={t(l.mfa.chooseAuthenticationMethod.useEmail)}
            name={AuthMethod.Email}
            onChange={onSelect}
            checked={selectedAuthMethod === AuthMethod.Email}
            value={AuthMethod.Email}
            disabled={isEmailDisabled}
            input="radio"
            label={t(l.mfa.chooseAuthenticationMethod.useEmail)}
            customCss={styles.tile}
          >
            <Tooltip
              tooltipContent={t(l.mfa.chooseAuthenticationMethod.emailMFAAlreadyEnabled)}
              isTooltipHidden={!isEmailDisabled}
            >
              <Typography>
                {t(l.mfa.chooseAuthenticationMethod.receiveOneTimeCodeViaEmail)}
              </Typography>
            </Tooltip>
          </SelectableTile>
        );
      case AuthMethod.App:
        return (
          <SelectableTile
            aria-label={t(l.mfa.chooseAuthenticationMethod.useAuthenticatorApp)}
            name={AuthMethod.App}
            onChange={onSelect}
            checked={selectedAuthMethod === AuthMethod.App}
            value={AuthMethod.App}
            input="radio"
            label={t(l.mfa.chooseAuthenticationMethod.useAuthenticatorApp)}
            customCss={styles.tile}
            rightContent={
              <Pill size="small" intention="neutral">
                {t(l.mfa.chooseAuthenticationMethod.moreSecure)}
              </Pill>
            }
          >
            <Typography>
              {t(l.mfa.chooseAuthenticationMethod.generateCodeViaAuthenticatorApp)}
            </Typography>
          </SelectableTile>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderAuthMethod(AuthMethod.Sms)}
      {renderAuthMethod(AuthMethod.Email)}
      {renderAuthMethod(AuthMethod.App)}
    </>
  );
};

export { ChooseAuthenticationMethod };
